/**
 * toy-object — the canonical "button" of the toy shell.
 *
 * It is not a rectangle with text: it is an illustrated OBJECT that is touched. Each
 * touch responds with the toy's three feedback layers:
 *   1. SOUND     — pentatonic pop with the voice (note) unique to the object.
 *   2. ANIMATION — squash-and-stretch bounce (CSS, GPU-only).
 *   3. HAPTICS   — short pulse wherever the platform supports it.
 *
 * The accessible name goes in `label` (the button's aria-label); the art is
 * decorative. With prefers-reduced-motion the bounce does not trigger (sound
 * and haptics remain: they are not movement).
 */

import { createSignal, type JSX, onCleanup } from "solid-js";
import { playTapPop } from "@/lib/toy/ui-sfx";
import { prefersReducedMotion, TOY_BOUNCE_MS } from "@/lib/toy/motion";
import { vibrate } from "@/lib/gestures/haptics";
import { useSettings } from "@/providers/settings-provider";

interface ToyObjectProps {
  /** Accessible name of the object (e2e tests and readers depend on it). */
  label: string;
  /** Pop note (pentatonic index) — each object has its own voice. */
  tone?: number;
  /** Organic tilt in degrees (the object "lives" in the scene). */
  tilt?: number;
  /** Entry pop-in delay in ms (scene stagger). */
  appearDelayMs?: number;
  class?: string;
  children: JSX.Element;
  /** Receives the event so the scene can launch its transition from the touch. */
  onActivate: (event: MouseEvent) => void;
}

export function ToyObject(props: ToyObjectProps): JSX.Element {
  const settings = useSettings();
  const [bouncing, setBouncing] = createSignal(false);
  let bounceTimer: ReturnType<typeof setTimeout> | undefined;

  onCleanup(() => {
    clearTimeout(bounceTimer);
  });

  const handleTap = (event: MouseEvent) => {
    playTapPop(settings.sfxVolume(), props.tone ?? 0);
    vibrate(15);
    if (!prefersReducedMotion()) {
      setBouncing(false);
      // Reset on the next frame so an impatient touch re-triggers
      // the animation instead of leaving it frozen halfway.
      requestAnimationFrame(() => setBouncing(true));
      clearTimeout(bounceTimer);
      bounceTimer = setTimeout(() => setBouncing(false), TOY_BOUNCE_MS);
    }
    props.onActivate(event);
  };

  return (
    <button
      type="button"
      aria-label={props.label}
      onClick={handleTap}
      class={`toy-object ${props.class ?? ""}`}
      style={{ "--toy-tilt": `${props.tilt ?? 0}deg` }}
    >
      {/* El pop-in de entrada va en un wrapper INTERNO: si escalara el botón,
          su caja medible (targets táctiles) encogería durante la animación. */}
      <span
        class="animate-pop-in block h-full w-full"
        style={{ "animation-delay": `${props.appearDelayMs ?? 0}ms` }}
        aria-hidden="true"
      >
        <span class={`toy-object-body ${bouncing() ? "toy-bouncing" : ""}`}>{props.children}</span>
      </span>
    </button>
  );
}
